// components/player/VideoPlayer.tsx
// Hybrid YouTube Control System v2 — Loop Trick + Visual Shield + Pre-emptive Pause
// Updated for Gen Z redesign with mode support + new palette
import { useRef, useEffect, useState, useCallback } from 'react';
import { ScreenOrientation } from '@capacitor/screen-orientation';

interface VideoPlayerProps {
  videoId: string;
  mode?: 'course' | 'explore';
  onTimeUpdate?: (time: number) => void;
  onEnded?: () => void;
  onNext?: () => void;
  onBackToTopics?: () => void;
}

// Extend Window for YouTube Iframe API globals
declare global {
  interface Window {
    onYouTubeIframeAPIReady: () => void;
    YT: any;
  }
}

export default function VideoPlayer({ videoId, onTimeUpdate, onEnded, onNext, onBackToTopics }: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerDivRef = useRef<HTMLDivElement>(null);
  const ytPlayer = useRef<any>(null);
  const monitorRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasEndedRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(100);
  const [isLandscape, setIsLandscape] = useState(false);

  // ─── YouTube API State Change Handler ───────────────────────────────
  const handleStateChange = useCallback((event: any) => {
    if (!window.YT) return;
    const state = event.data;

    if (state === window.YT.PlayerState.PLAYING) {
      setIsPlaying(true);
      const dur = event.target.getDuration();
      if (dur > 0) setDuration(dur);
    } else if (state === window.YT.PlayerState.PAUSED) {
      setIsPlaying(false);
    } else if (state === window.YT.PlayerState.ENDED) {
      if (!hasEndedRef.current) {
        hasEndedRef.current = true;
        setShowOverlay(true);
        setIsPlaying(false);
        onEnded?.();
      }
    }
  }, [onEnded]);

  // ─── Load YouTube API + Initialize Player ───────────────────────────
  useEffect(() => {
    hasEndedRef.current = false;
    setShowOverlay(false);
    setIsReady(false);
    setCurrentTime(0);
    setDuration(0);

    if (!document.getElementById('yt-iframe-api-script')) {
      const tag = document.createElement('script');
      tag.id = 'yt-iframe-api-script';
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }

    const createPlayer = () => {
      if (!playerDivRef.current) return;
      if (ytPlayer.current) {
        try { ytPlayer.current.destroy(); } catch (_) { /* noop */ }
        ytPlayer.current = null;
      }

      ytPlayer.current = new window.YT.Player(playerDivRef.current, {
        videoId,
        playerVars: {
          loop: 1,
          playlist: videoId,
          autoplay: 1,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          disablekb: 1,
          fs: 0,
          playsinline: 1,
          iv_load_policy: 3,
          cc_load_policy: 0,
          showinfo: 0,
          origin: window.location.origin,
        },
        events: {
          onReady: (event: any) => {
            const dur = event.target.getDuration();
            if (dur > 0) setDuration(dur);
            setIsReady(true);
            setVolume(event.target.getVolume());
            setIsMuted(event.target.isMuted());
          },
          onStateChange: handleStateChange,
        },
      });
    };

    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      const prevCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prevCallback) prevCallback();
        createPlayer();
      };
    }

    return () => {
      if (ytPlayer.current) {
        try { ytPlayer.current.destroy(); } catch (_) { /* noop */ }
        ytPlayer.current = null;
      }
      if (monitorRef.current) {
        clearInterval(monitorRef.current);
        monitorRef.current = null;
      }
    };
  }, [videoId, handleStateChange]);

  // ─── Time Monitor: Pre-emptive Pause + Loop Intercept ───────────────
  useEffect(() => {
    monitorRef.current = setInterval(() => {
      if (!ytPlayer.current || typeof ytPlayer.current.getCurrentTime !== 'function') return;

      const now = ytPlayer.current.getCurrentTime();
      const dur = ytPlayer.current.getDuration();

      setCurrentTime(now);
      if (dur > 0) setDuration(dur);
      onTimeUpdate?.(now);

      if (dur > 0 && now >= dur - 20 && !hasEndedRef.current) {
        hasEndedRef.current = true;
        ytPlayer.current.pauseVideo();
        setShowOverlay(true);
        setIsPlaying(false);
        onEnded?.();
      }
    }, 250);

    return () => {
      if (monitorRef.current) {
        clearInterval(monitorRef.current);
        monitorRef.current = null;
      }
    };
  }, [onTimeUpdate, onEnded]);

  // ─── Player Control Handlers ────────────────────────────────────────
  const handlePlayPause = () => {
    if (!ytPlayer.current || showOverlay) return;
    if (isPlaying) {
      ytPlayer.current.pauseVideo();
    } else {
      ytPlayer.current.playVideo();
    }
  };

  const handleSeek = (newTime: number) => {
    if (!ytPlayer.current || showOverlay) return;
    ytPlayer.current.seekTo(newTime, true);
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (newVol: number) => {
    if (!ytPlayer.current) return;
    ytPlayer.current.setVolume(newVol);
    setVolume(newVol);
    if (newVol === 0) {
      ytPlayer.current.mute();
      setIsMuted(true);
    } else if (isMuted) {
      ytPlayer.current.unMute();
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    if (!ytPlayer.current) return;
    if (isMuted) {
      ytPlayer.current.unMute();
      setIsMuted(false);
    } else {
      ytPlayer.current.mute();
      setIsMuted(true);
    }
  };

  const handleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(() => { /* noop */ });
    } else {
      document.exitFullscreen();
    }
  };

  const toggleOrientation = async () => {
    try {
      if (isLandscape) {
        // @ts-ignore
        await ScreenOrientation.lock({ orientation: 'portrait' });
        setIsLandscape(false);
      } else {
        // @ts-ignore
        await ScreenOrientation.lock({ orientation: 'landscape' });
        setIsLandscape(true);
      }
    } catch (err) {
      console.warn('ScreenOrientation error:', err);
      // Fallback for browsers that support screen.orientation but not Capacitor plugin
      try {
        if (!isLandscape && document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
          // @ts-ignore
          await screen.orientation.lock('landscape');
          setIsLandscape(true);
        } else if (isLandscape) {
          screen.orientation.unlock();
          document.exitFullscreen();
          setIsLandscape(false);
        }
      } catch (e) {
        console.warn('Web orientation fallback failed', e);
      }
    }
  };

  const replayVideo = () => {
    hasEndedRef.current = false;
    setShowOverlay(false);
    if (ytPlayer.current) {
      ytPlayer.current.seekTo(0, true);
      ytPlayer.current.playVideo();
    }
  };

  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className="yt-player-root"
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* ── Video Area ─────────────────────────────────────────────── */}
      <div className="yt-player-viewport">
        <div className="yt-player-iframe-wrap">
          <div ref={playerDivRef} className="w-full h-full" />
        </div>

        {/* ── Visual Shield ───────────────────────────────────────── */}
        <div
          className="yt-player-shield"
          onClick={handlePlayPause}
        />

        {/* ── Loading Indicator ────────────────────────────────────── */}
        {!isReady && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-black">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-mint-400 border-t-transparent rounded-full animate-spin" />
              <span className="text-surface-300 text-sm">Loading lesson…</span>
            </div>
          </div>
        )}

        {/* ── Lesson Completed Overlay ─────────────────────────────── */}
        {showOverlay && (
          <div className="absolute inset-0 z-40 bg-black/95 flex flex-col justify-center items-center backdrop-blur-sm px-6">
            <div className="text-center">
              <div className="text-5xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold text-white mb-2">Lesson Complete!</h2>
              <p className="text-surface-300 mb-8 text-sm">Great work! What would you like to do next?</p>
              <div className="flex flex-col gap-3 w-full max-w-xs mx-auto">
                <button
                  onClick={() => onNext?.()}
                  className="touch-btn gradient-mint text-white font-semibold py-3.5 text-sm shadow-lg"
                >
                  Next Lesson →
                </button>
                <button
                  onClick={replayVideo}
                  className="touch-btn bg-surface-800 border border-surface-700 text-white font-medium py-3 text-sm"
                >
                  ↻ Rewatch
                </button>
                <button
                  onClick={() => onBackToTopics?.()}
                  className="text-xs text-surface-300 hover:text-white transition-colors py-2"
                >
                  Back to subjects
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Custom Controls Bar ────────────────────────────────────── */}
      <div className="yt-player-controls">
        {/* Progress Bar */}
        <div
          className="yt-progress-bar-track"
          onClick={(e) => {
            if (showOverlay) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const ratio = (e.clientX - rect.left) / rect.width;
            handleSeek(ratio * duration);
          }}
        >
          <div
            className="yt-progress-bar-fill"
            style={{ width: `${progressPercent}%` }}
          />
          <div
            className="yt-progress-bar-thumb"
            style={{ left: `${progressPercent}%` }}
          />
        </div>

        {/* Bottom Row */}
        <div className="flex items-center gap-3 px-3 pb-2 pt-1">
          {/* Play / Pause */}
          <button
            onClick={handlePlayPause}
            className="yt-ctrl-btn"
            disabled={showOverlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
            )}
          </button>

          {/* Volume */}
          <button onClick={toggleMute} className="yt-ctrl-btn" aria-label={isMuted ? 'Unmute' : 'Mute'}>
            {isMuted || volume === 0 ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.8 8.8 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
            ) : volume < 50 ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.5 12A4.5 4.5 0 0016 8.97v6.06c1.48-.73 2.5-2.25 2.5-4.03zM5 9v6h4l5 5V4L9 9H5z"/></svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 8.97v6.06c1.48-.73 2.5-2.25 2.5-4.03zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77 0-4.28-2.99-7.86-7-8.77z"/></svg>
            )}
          </button>
          <input
            type="range"
            min={0}
            max={100}
            value={isMuted ? 0 : volume}
            onChange={(e) => handleVolumeChange(Number(e.target.value))}
            className="w-20 h-1 bg-surface-600 rounded appearance-none cursor-pointer accent-mint-400"
          />

          {/* Time */}
          <span className="text-xs font-mono text-surface-300 whitespace-nowrap select-none ml-auto">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          {/* Rotate Screen */}
          <button onClick={toggleOrientation} className="yt-ctrl-btn ml-2" aria-label="Rotate Screen">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          {/* Fullscreen */}
          <button onClick={handleFullscreen} className="yt-ctrl-btn" aria-label="Fullscreen">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}
